import { Grid, Skeleton, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ElementItemProps } from "../types";
import { useDevice } from "../../../../utils/DeviceProvider";
import { ChkboxElement } from "./Chkbox";
import Info from "./Info";
import { ComboxElement } from "./Combox";
import { RestoreButton, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";
import { isEqual } from "lodash-es";

interface VEHCANV12Parameters {
  version: number;
  index: number;
}

interface VEHCANV12Value {
  enabled: boolean;
  channel: string;
  canbus: { busName: string; driverData: string };
}

const VEHCANV12 = ({ version, configKey, elementKey, index, disabled }: Omit<ElementItemProps<VEHCANV12Parameters>, "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<VEHCANV12Value | null>(null);
  const [savedCanbus, setSavedCanbus] = useState("");
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [channel, setChannel] = useState("");
  const [canbus, setCanbus] = useState("");

  const { formatMessage } = useIntl();
  const { openContextMenu } = useResetContextMenu();

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();

  const channelItems = useMemo(() => Array.from({ length: 16 }).map((_, index) => ({ value: `${index}`, label: `${index}` })), []);
  const canbuses = useMemo(
    () =>
      Object.fromEntries(
        [
          { busName: "FiatScudo", driverData: "yA9A948AFCDACAEAFA75EDDA3A4A5A558474645444342" },
          { busName: "CitroenJumpy", driverData: "yA9A948AFCDACAEAFA75EDDA3A4A5A558474645444342" },
          { busName: "PeugeotPartner", driverData: "yA9A948AFCDACAEAFA75EDDA3A4A5A558474645444342" },
          { busName: "Fiat2008", driverData: "yA9A95EAA2CAEAEAFA85EDDA3A4A5A358474645444342" },
          { busName: "FiatUno", driverData: "yA9A95EAA2CAEAEAFA85EDDA3A4A5A358474645444342" },
          { busName: "FiatDucato", driverData: "yA9A95EAA2CAEAEAFA85EDDA3A4A5A358474645444342" },
          { busName: "ThermoWatch12_5K", driverData: "yA9A9D7AB9CADAEAFA8A35D5C5B5A5958474645444342" },
          { busName: "ThermoWatch50K", driverData: "yA9A95EAA9CADAEAFA8A35D5C5B5A5958474645444342" },
        ].map((item, index) => [`${index}`, item])
      ),
    []
  );
  const canItems = useMemo(() => Object.entries(canbuses).map(([value, { busName: label }]) => ({ value, label })), [canbuses]);

  const onChange = useCallback(
    ({ enabled, channel, canbus }: { enabled?: boolean; channel?: string; canbus?: string }) => {
      if (configIndex === null) {
        return;
      }
      if (!currentValue[configKey].items[configIndex].value) {
        currentValue[configKey].items[configIndex].value = {};
      }
      if (enabled !== undefined) {
        currentValue[configKey].items[configIndex].value.enabled = enabled;
      }
      if (channel !== undefined) {
        currentValue[configKey].items[configIndex].value.channel = channel;
      }
      if (canbus !== undefined) {
        currentValue[configKey].items[configIndex].value.canbus = canbuses[canbus];
      }
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex, canbuses]
  );

  const onEnabledChange = useCallback(
    (value: boolean) => {
      setEnabled(value);
      onChange({ enabled: value });
    },
    [onChange]
  );

  const getCanbusValue = useCallback(
    (canbus?: { busName?: string; driverData: string }) => {
      if (!canbus) {
        return "";
      }
      return (
        Object.entries(canbuses).find(
          ([_, { busName, driverData }]) => (canbus.busName ? busName === canbus.busName : true) && driverData === canbus.driverData
        )?.[0] ?? ""
      );
    },
    [canbuses]
  );

  const resetCanbus = useCallback(() => {
    if (!config || configIndex === null) {
      return;
    }
    const saved = config.toolboxConfig[configKey].items[configIndex].value.canbus;
    const comboValue = getCanbusValue(saved);
    setCanbus(comboValue);
    currentValue[configKey].items[configIndex].value.canbus = saved;
    setCurrentValue(currentValue);
  }, [config, configIndex, configKey, currentValue, getCanbusValue, setCurrentValue]);

  const isCanbusEdited = useCallback(() => {
    return savedCanbus !== canbus;
  }, [savedCanbus, canbus]);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(
      ({ type, key }: { type: string; key: string }) => type === `VEHCANV${version}` && (key === elementKey || `${key}` === elementKey)
    ) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, version, index, elementKey]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex].value ?? null;
    setSaved(svd);
    setSavedCanbus(svd ? getCanbusValue(svd.canbus) : "");
    const { enabled, channel, canbus } = currentValue[configKey].items[configIndex]?.value ?? {};
    setEnabled(enabled);
    setChannel(channel ?? "");
    const canbusValue = getCanbusValue(canbus);
    setCanbus(canbusValue ?? "");
  }, [active, currentValue, config, configIndex, configKey, getCanbusValue, canbuses]);

  if (enabled === null) {
    return (
      <Grid container spacing={1}>
        <Grid item>
          <Info text={`${index + 1}.`} inlineTitle />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
        </Grid>
      </Grid>
    );
  }

  if (configIndex === -1) {
    return <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>;
  }

  return (
    <Grid container spacing={1} sx={{ alignItems: "flex-end" }}>
      <Grid item>
        <Info text={`${index + 1}.`} inlineTitle />
      </Grid>
      <Grid item>
        <ChkboxElement
          value={enabled}
          label={formatMessage({ defaultMessage: "Enabled" })}
          onChange={onEnabledChange}
          edited={saved?.enabled !== enabled}
          disabled={disabled}
          onContextMenu={saved && saved.enabled !== enabled ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.enabled`) : undefined}
        />
      </Grid>
      <Grid item>
        <ComboxElement
          value={channel ?? ""}
          label={formatMessage({ defaultMessage: "Channel" })}
          items={channelItems}
          onChange={setChannel}
          onBlur={() => onChange({ channel })}
          edited={(saved?.channel ?? "") !== channel}
          disabled={disabled}
          onContextMenu={saved && saved.channel !== channel ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.channel`) : undefined}
        />
      </Grid>
      <Grid item>
        <ComboxElement
          value={canbus ?? ""}
          label={formatMessage({ defaultMessage: "CAN bus" })}
          items={canItems}
          onChange={setCanbus}
          onBlur={() => onChange({ canbus })}
          edited={savedCanbus !== canbus}
          disabled={disabled}
          sx={{ width: 220 }}
          onContextMenu={
            saved && savedCanbus !== canbus
              ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.canbus`, { onReset: resetCanbus, isEdited: isCanbusEdited })
              : undefined
          }
        />
      </Grid>
      {(
        !saved
          ? enabled || channel || canbus
          : !isEqual(saved ? { enabled: saved.enabled, channel: saved.channel, canbus: savedCanbus } : saved, { enabled, channel, canbus })
      ) ? (
        <Grid item>
          <RestoreButton path={`${configKey}.items.${configIndex}.value`} saved={saved} />
        </Grid>
      ) : null}
    </Grid>
  );
};

export default VEHCANV12;
